/**
 * <FamilyChat /> — closed-group WebSocket chat for a family tree.
 *
 * Only visible to invited members. Owner/admin can mute or remove members.
 * Auto-reconnects on transient websocket errors; falls back to REST-only
 * mode if the websocket never opens.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Loader2, MessageCircle, VolumeX, Volume2, UserMinus, Trash2, WifiOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function wsUrl(treeId, token) {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/family-trees/${treeId}/chat?token=${encodeURIComponent(token)}`;
}

export default function FamilyChat({ treeId, canModerate = false, isMember = false }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState("");
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const reconnectTimer = useRef(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: hist }, { data: mems }] = await Promise.all([
        http.get(`/family-trees/${treeId}/chat`),
        http.get(`/family-trees/${treeId}/chat/members`),
      ]);
      setMessages(hist.messages || []);
      setMembers(mems.members || []);
      setErrorState("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't load chat.";
      setErrorState(detail);
    } finally { setLoading(false); }
  }, [treeId]);

  const connect = useCallback(async () => {
    if (!isMember) return;
    try {
      const { data } = await http.get(`/family-trees/${treeId}/chat/ws-token`);
      if (!data?.token) return;
      const ws = new WebSocket(wsUrl(treeId, data.token));
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        // Auto-reconnect after 3s if the component's still mounted.
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(() => connect(), 3000);
      };
      ws.onerror = () => setConnected(false);
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "message") {
            setMessages((cur) => [...cur, payload.message]);
          } else if (payload.type === "message_deleted") {
            setMessages((cur) => cur.map((m) => m.id === payload.message_id ? { ...m, deleted: true } : m));
          } else if (payload.type === "member_muted") {
            setMembers((cur) => cur.map((m) => m.user_id === payload.user_id ? { ...m, muted: true } : m));
          } else if (payload.type === "member_unmuted") {
            setMembers((cur) => cur.map((m) => m.user_id === payload.user_id ? { ...m, muted: false } : m));
          } else if (payload.type === "member_removed") {
            setMembers((cur) => cur.map((m) => m.user_id === payload.user_id ? { ...m, removed: true } : m));
          } else if (payload.type === "error") {
            toast.error(payload.detail || "Chat error.");
          }
        } catch { /* ignore */ }
      };
    } catch (e) {
      // Non-fatal — user still has REST posting.
      setConnected(false);
    }
  }, [treeId, isMember]);

  useEffect(() => {
    if (!isMember) return undefined;
    loadHistory();
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close?.();
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeId, isMember]);

  useEffect(() => {
    // Auto-scroll on new message.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setText("");
    // Prefer WebSocket for lower latency; fall back to REST if socket closed.
    if (wsRef.current && wsRef.current.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "message", text: t }));
    } else {
      try { await http.post(`/family-trees/${treeId}/chat`, { text: t }); }
      catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't send message.");
        setText(t); // restore so the user doesn't lose their draft
      }
    }
  };

  const delMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await http.delete(`/family-trees/${treeId}/chat/${id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  const mute = async (uid, on) => {
    try {
      await http.post(`/family-trees/${treeId}/chat/${on ? "mute" : "unmute"}`, { user_id: uid });
      toast.success(on ? "Muted." : "Unmuted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remove this member from the tree and chat? They'll lose access.")) return;
    try {
      await http.post(`/family-trees/${treeId}/chat/remove`, { user_id: uid });
      toast.success("Member removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove."); }
  };

  if (!isMember) {
    return (
      <div className="wofy-card text-sm text-center py-8" data-testid="family-chat-locked" style={{ color: "var(--wofy-text-soft)" }}>
        <MessageCircle className="w-6 h-6 mx-auto mb-2 opacity-60" />
        This chat is private to invited tree members. Ask the tree owner to add you.
      </div>
    );
  }

  return (
    <div data-testid="family-chat" className="wofy-card">
      <header className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <MessageCircle size={12} className="inline -mt-0.5 mr-1" /> FAMILY CHAT
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            {members.length} member{members.length === 1 ? "" : "s"}
            {connected ? (
              <span className="ml-2 text-green-700 text-xs">● live</span>
            ) : (
              <span className="ml-2 text-amber-700 text-xs"><WifiOff className="inline w-3 h-3" /> reconnecting…</span>
            )}
          </div>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="h-72 overflow-y-auto rounded-lg border p-3 space-y-3 text-sm mb-3"
        style={{ background: "#FAF7F0", borderColor: "var(--wofy-line)" }}
        data-testid="family-chat-scroll"
      >
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : errorState ? (
          <div className="text-center text-red-600 text-xs py-8">{errorState}</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            No messages yet — say hi to your family!
          </div>
        ) : messages.map((m) => (
          <div key={m.id} className={`flex gap-2 ${m.user_id === user?.id ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
            {m.avatar_url ? (
              <img src={m.avatar_url} alt={m.name} className="w-7 h-7 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full text-[10px] flex items-center justify-center shrink-0" style={{ background: "var(--wofy-primary)", color: "white" }}>
                {(m.name || "?").slice(0,1).toUpperCase()}
              </div>
            )}
            <div className={`max-w-[75%] ${m.user_id === user?.id ? "text-right" : ""}`}>
              <div className="text-[10px] opacity-60">{m.name || "Anon"} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              <div
                className={`inline-block rounded-2xl px-3 py-2 mt-0.5 ${m.deleted ? "italic opacity-50" : ""}`}
                style={{
                  background: m.user_id === user?.id ? "var(--wofy-primary)" : "white",
                  color: m.user_id === user?.id ? "white" : "var(--wofy-text)",
                  border: "1px solid var(--wofy-line)",
                }}
              >
                {m.deleted ? "[deleted]" : m.text}
              </div>
              {!m.deleted && (m.user_id === user?.id || canModerate) && (
                <button onClick={() => delMessage(m.id)} className="text-[10px] opacity-50 hover:opacity-100 ml-1" data-testid={`chat-del-${m.id}`}>
                  <Trash2 className="inline w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={send} className="flex gap-2" data-testid="chat-form">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={4000}
          placeholder="Type a message…"
          className="wofy-input flex-1"
          data-testid="chat-input"
        />
        <button type="submit" className="wofy-btn-primary" disabled={!text.trim()} data-testid="chat-send">
          <Send className="w-4 h-4" />
        </button>
      </form>

      {canModerate && members.length > 0 && (
        <details className="mt-4 text-xs" data-testid="chat-moderation-panel">
          <summary className="cursor-pointer opacity-70">Moderation ({members.filter((m) => !m.removed).length} active)</summary>
          <div className="mt-2 space-y-1">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-2" data-testid={`chat-member-${m.user_id}`}>
                <span className="flex-1">
                  {m.name || m.user_id}
                  {m.muted && <span className="ml-1 text-amber-700">muted</span>}
                  {m.removed && <span className="ml-1 text-red-600">removed</span>}
                </span>
                {!m.removed && (
                  <>
                    <button onClick={() => mute(m.user_id, !m.muted)} className="wofy-btn-ghost text-xs" data-testid={`chat-mute-${m.user_id}`}>
                      {m.muted ? <><Volume2 className="w-3 h-3" /> Unmute</> : <><VolumeX className="w-3 h-3" /> Mute</>}
                    </button>
                    <button onClick={() => removeMember(m.user_id)} className="wofy-btn-ghost text-xs text-red-600" data-testid={`chat-remove-${m.user_id}`}>
                      <UserMinus className="w-3 h-3" /> Remove
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
